import { AuthModeEnum } from "~/utils/enums/auth.enums";
import { defineStore } from "pinia";
import {
  GroupsApi,
  UserImportApi,
  UsersApi,
  UserDetailApi,
  AdminUsersApi,
  AuthTeamsApi,
  GetLastUsersImportStateStatusEnum,
  type CollaboratorDetails,
  type ImportsResponse,
  type UploadUsersCSVRequest,
  type CollaboratorsPagination,
  type AvailableUserResponse,
  type UserCompanyModel,
  type UserCompanyPagination,
} from "@simbelapp/order-sdk";
import { ItemType } from "@simbelapp/order-sdk";
import type { INotExcludedUser, IUsersStore } from "~/utils/interfaces/users-interfaces";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { usePagination } from "~/composables/paginations/usePagination";
import { useFilters } from "~/composables/filters/useFilters";
import { toCompanyUser } from "~/utils/helpers/users/company-user";
import { useAuthStore } from "~/store/auth/auth.store";
import { AUTH_REQUIRED_MESSAGE } from "~/utils/helpers/auth/oidc-contract";

export const useUsersStore = defineStore("users", {
  state: (): IUsersStore => {
    return {
      companyUsers: null,
      companyUsersManager: null,
      companyUsersGroupAdmin: null,
      companyUsersFullDatas: null,
      allUsers: null,
      userGroups: null,
      showNotifyUsers: false,
      showCsvImportModal: false,
      showDisableSsoModal: false,
      selectedUsersIdList: [],
      excludedUsersIdList: [],
      showCsvExportModal: false,
      showEndUserModal: false,
      showGroupsUsersModal: false,
    };
  },
  getters: {},
  actions: {
    /**
     * Fetches the company users from the API.
     *
     * Uses `/users/company-users`, the twin of `/users/company`: same query parameters, same
     * `UserCompanyPagination` model. Nothing in the app calls `/users/company`.
     *
     * @param enableRolesFilters - Flag indicating whether to get the list depending of the user role.
     */
    async fetchCompanyUsers(
      enableRolesFilters = true,
      lazyLoad = false,
      search?: string,
      usersIds?: string[],
    ): Promise<UserCompanyPagination> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);

      return await apiInstance.getUsersCompany({
        enableRolesFilters,
        lazyLoad,
        ...(search && { search }),
        ...(usersIds?.length && { filters: [`user_ids=${usersIds.join(",")}`] }),
      });
    },

    async fetchCompanyUsersLazyWithStore(enableRolesFilters = true, search?: string, usersIds?: string[]) {
      const data = await this.fetchCompanyUsers(enableRolesFilters, true, search, usersIds);

      this.companyUsers = data?.data ?? [];
    },

    async fetchCompanyUsersManager() {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
      const data = await apiInstance.getUsersByManager();

      if (data) {
        this.companyUsersManager = data;
      }
    },

    async fetchCompanyUsersGroupAdmin() {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
      const data = await apiInstance.getUsersByGroupAdmin();

      if (data) {
        this.companyUsersGroupAdmin = data;
      }
    },

    async fetchCompanyUsersFullDatas() {
      const pagination = usePagination();
      const filters = useFilters();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();

      const data: CollaboratorsPagination = await apiInstance.getCollaborators({
        ...queryString,
        ...filtersQuery,
        sortingColumn: paginateOptions.sortingColumn,
        excludeDisabledUsers: "false",
      });

      if (data) {
        this.companyUsersFullDatas = data;
      }
    },
    async fetchCompanyUsersFullDatasAddUsers(
      paginationOptions?: any,
      context?: string,
      itemId?: string,
      itemType?: ItemType,
    ) {
      const pagination = usePagination();
      const filters = useFilters(context);
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
      let transformQuery = {};

      if (paginationOptions) {
        const paginateQuery = new URLSearchParams(paginationOptions);
        transformQuery = Object.fromEntries(paginateQuery.entries());
      } else {
        // Fallback for existing calls
        const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
        const paginateQuery = new URLSearchParams(paginateOptions);
        transformQuery = Object.fromEntries(paginateQuery.entries());
      }

      const filtersQuery = filters.getFiltersForSDK();

      const data: CollaboratorsPagination = await apiInstance.getCollaboratorsForAddAttendees({
        ...(itemId && { itemId }),
        ...(itemType && { itemType }),
        ...transformQuery,
        ...filtersQuery,
        excludeDisabledUsers: "true",
      });

      if (data) {
        this.companyUsersFullDatas = data;
      }
    },
    async exportUsers(format: string): Promise<boolean> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        await apiInstance.exportUsers({
          format,
        });
        return true;
      } catch {
        feedback.error("Une erreur est survenue", "small");
        return false;
      }
    },
    async fetchUser(userId: string): Promise<CollaboratorDetails | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const data = await apiInstance.getUserDetailGeneral({ userId });
        return data ?? null;
      } catch {
        return null;
      }
    },

    async fetchMyUserDetails(): Promise<UserCompanyModel | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        const data = await apiInstance.getUserDetailOwnDetails();
        return data ?? null;
      } catch (error) {
        return null;
      }
    },

    async fetchUserGroups(userId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);
        const data = await apiInstance.getUserGroups({ userId });
        if (data) {
          this.userGroups = data;
        } else {
          this.userGroups = null;
        }
      } catch {
        feedback.error("Une erreur est survenue", "small");
        this.userGroups = null;
      }
    },

    /**
     * Who "Tout sélectionner" actually resolves to: everyone the grid's filter bar (`context`) and
     * search (`paginateOptions`) match, minus `excludedUserIds`. Leave either out and the selection
     * reaches users the grid never listed.
     *
     * Since order-sdk 10.3.0-beta.5 the route also answers `managerEmail` / `managerDisplayValue`,
     * so a manager convocation resolves from this call alone — no second lookup. `toCompanyUser`
     * normalizes the rows.
     */
    async fetchUsersNotExcluded({
      excludedUserIds,
      context,
      enableRolesFilters = true,
      paginateOptions,
    }: {
      excludedUserIds: string[];
      context?: string;
      enableRolesFilters?: boolean;
      paginateOptions?: Record<string, any>;
    }): Promise<AvailableUserResponse[] | null> {
      const feedback = useFeedback();
      const filters = useFilters(context);

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        return await apiInstance.getUsersNotExcluded({
          // `offset` / `limit` ride as strings, as the generated request type declares them.
          ...(paginateOptions ? Object.fromEntries(new URLSearchParams(paginateOptions).entries()) : {}),
          ...filters.getFiltersForSDK(),
          ...(paginateOptions?.sortingColumn && { sortingColumn: paginateOptions.sortingColumn }),
          // The SDK sends an empty array as `usersList=`, which orders reads as one empty id and rejects.
          ...(excludedUserIds.length > 0 && { usersList: excludedUserIds }),
          enableRolesFilters,
        });
      } catch {
        feedback.error(`erreur`, "small");
        return null;
      }
    },

    async fetchNotExcludedUser(userIdList: string[]): Promise<INotExcludedUser[] | null> {
      const pagination = usePagination();

      const users = await this.fetchUsersNotExcluded({
        excludedUserIds: userIdList,
        paginateOptions: pagination.getFindOptions(pagination.paginateOptions.value),
      });
      // camelCase payload, snake_case store: normalized here so every consumer reads one shape.
      // Emptied on failure, so no bulk action falls back on a previous selection.
      this.selectedUsersIdList = users?.map(toCompanyUser) ?? [];
      return users ? this.selectedUsersIdList : null;
    },

    async importUsers(uploadUsersCSV: UploadUsersCSVRequest): Promise<ImportsResponse | null> {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserImportApi);
      const response: ImportsResponse | null = await apiInstance.uploadUsersCSV(uploadUsersCSV).catch(async (error) => {
        const errorResponse = await error.response.json();
        if (errorResponse.message.includes("Parallel import in progress")) {
          feedback.error("Un import est déjà en cours, veuillez attendre avant de relancer", "small");
          return null;
        } else {
          feedback.error("Erreur Interne", "small");
          return null;
        }
      });
      if (response) {
        return response;
      } else {
        return null;
      }
    },

    async findLastOneCSVImport(status?: GetLastUsersImportStateStatusEnum): Promise<ImportsResponse | null> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserImportApi);
      const response: ImportsResponse | null = await apiInstance.getLastUsersImportState({ status });
      if (response) {
        return response;
      } else {
        return null;
      }
    },
    async notifyUsers(userIds: string[], message: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        await apiInstance.notifyUser({
          notifyUsersDto: { userIds, message },
        });
        return true;
      } catch {
        feedback.error(`erreur`, "small");
        return false;
      }
    },

    async setUserLanguage(language: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        await apiInstance.changeLanguage({
          changeLanguageDto: { language },
        });
      } catch (error) {
        console.error("Error changing user language:", error);
        throw error;
      }
    },

    async updateNames(usernamePayload: { firstname: string; lastname: string }) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        return await apiInstance.updateUserName({
          setUserNameDto: {
            firstname: usernamePayload.firstname,
            lastname: usernamePayload.lastname,
          },
        });
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return null;
      }
    },

    async fetchAdminAllUsers() {
      const feedback = useFeedback();
      const authStore = useAuthStore();
      if (authStore.mode !== AuthModeEnum.LEGACY && authStore.mode !== AuthModeEnum.OIDC) return;
      const pagination = usePagination();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminUsersApi);
        const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

        const data = await apiInstance.getAdminUsers(
          {
            search: paginateOptions.search,
            offset: paginateOptions.offset,
            limit: paginateOptions.limit,
            sortingColumn: paginateOptions.sortingColumn,
            order: paginateOptions.order,
          },
          // Legacy administrators use their refresh token; OIDC uses the SDK access-token transport.
          authStore.mode === AuthModeEnum.LEGACY
            ? { headers: { Authorization: `Bearer ${authStore.tokens?.refreshToken}` } }
            : undefined,
        );

        this.allUsers = data;
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async resetPasswordAsSimbelAdmin(userId: string, hashedPassword: string) {
      const feedback = useFeedback();
      const authStore = useAuthStore();

      try {
        const isOidc = authStore.mode === AuthModeEnum.OIDC;
        const administratorToken = isOidc
          ? authStore.tokens?.accessToken
          : authStore.mode === AuthModeEnum.LEGACY
            ? authStore.tokens?.refreshToken
            : null;
        if (!administratorToken) throw new Error(AUTH_REQUIRED_MESSAGE);

        // As with Log As, legacy refresh credentials must bypass the SDK's access-token transport.
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AuthTeamsApi, isOidc);

        await apiInstance.resetUserPasswordAsSimbelAdmin(
          {
            userId,
            adminResetPasswordDto: { password: hashedPassword },
          },
          {
            headers: {
              Authorization: `Bearer ${administratorToken}`,
              "Content-Type": "application/json",
            },
          },
        );
      } catch (error) {
        feedback.error("Impossible de réinitialiser le mot de passe", "small");
        throw error;
      }

      await this.fetchAdminAllUsers();
    },

    async disableSSO(disable: boolean, selectedIds: string[], selectAll: boolean, excludedIds?: string[]) {
      const feedback = useFeedback();
      const pagination = usePagination();
      const filters = useFilters();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersApi);
        const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
        const filtersQuery = filters.getFiltersForSDK();

        return await apiInstance.disableSso({
          disableSsoDto: { disable, selectedIds, excludedIds, selectAll },
          // when selectAll is true the back re-resolves the target set from the current search + filters
          search: paginateOptions.search,
          filters: filtersQuery.filters,
          sortingColumn: paginateOptions.sortingColumn,
          offset: paginateOptions.offset,
          limit: paginateOptions.limit,
          order: paginateOptions.order,
        });
      } catch (error) {
        feedback.error(`Une erreur est survenue`, "small");
        throw error;
      }
    },
    /**
     * @do add groups to users from groups company
     * @param usersIds as [user_id1, user_id2, ...]
     * @param groupsIds as [group_id1, group_id2, ...]
     * @param selectAll as boolean -> select all users
     * @param hasGroups as boolean -> add groups or remove groups
     * @returns {Promise<boolean>} false when the selection could not be resolved or the write failed
     */
    async addGroupsToUsers({
      usersIds,
      groupsIds,
      selectAll,
    }: {
      usersIds: string[];
      groupsIds: string[];
      selectAll: boolean;
    }): Promise<boolean> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);

      let usersExcluded: string[] = [];
      if (selectAll) {
        const selectedUsers = await this.fetchNotExcludedUser(usersIds);
        if (!selectedUsers) return false;
        usersExcluded = selectedUsers.map((user) => user.userId);
      }

      try {
        if (groupsIds.length) {
          await apiInstance.affectUsersToGroups({
            affectUsersGroupDto: {
              userIds: selectAll ? usersExcluded : usersIds,
              groupIds: groupsIds,
            },
          });
        } else {
          await apiInstance.removeUsersFromAllGroups({
            removeUsersDto: {
              userIds: selectAll ? usersExcluded : usersIds,
            },
          });
        }
        return true;
      } catch {
        return false;
      }
    },
  },
});
